import nodemailer from "nodemailer";

import { logger } from "../utils/logger";

// Email configuration interface
interface EmailConfig {
  host: string;
  port: number;
  secure: boolean;
  auth: {
    user: string;
    pass: string;
  };
}

// Email template interface
interface EmailTemplate {
  subject: string;
  html: string;
  text: string;
}

// OTP email data interface
interface OTPEmailData {
  name?: string;
  email: string;
  otp: string;
  purpose: "registration" | "password_reset";
  expiryMinutes: number;
}

class EmailService {
  private transporter: nodemailer.Transporter;
  private fromEmail: string;
  private appName: string;
  private isConnectionVerified = false;

  constructor() {
    this.fromEmail = process.env.FROM_EMAIL ?? "noreply@covalent.com";
    this.appName = process.env.APP_NAME ?? "Covalent";

    // Create transporter with flexible SMTP configuration (supports Gmail, cPanel, etc.)
    this.transporter = this.createTransporter();

    // Verify connection (non-blocking)
    void this.verifyConnection();
  }

  /**
   * Create SMTP transporter with proper configuration
   */
  private createTransporter(): nodemailer.Transporter {
    const smtpHost = process.env.SMTP_HOST ?? "smtp.gmail.com";
    const smtpPort = Number.parseInt(process.env.SMTP_PORT ?? "587");

    // Determine secure mode based on port and explicit setting
    // Port 465: secure=true (SSL/TLS)
    // Port 587: secure=false (STARTTLS)
    // Port 25/26: secure=false (usually plain or STARTTLS)
    let isSecure = process.env.SMTP_SECURE === "true";
    if (process.env.SMTP_SECURE === undefined) {
      // Auto-detect based on port if not explicitly set
      if (smtpPort === 465) {
        isSecure = true; // SSL for port 465
      } else if (smtpPort === 587 || smtpPort === 25 || smtpPort === 26) {
        isSecure = false; // STARTTLS for common SMTP ports
      }
    }

    // cPanel and other hosting providers may need different TLS settings
    const requireTLS = process.env.SMTP_REQUIRE_TLS === "true";
    const ignoreTLS = process.env.SMTP_IGNORE_TLS === "true";

    return nodemailer.createTransport({
      host: smtpHost,
      port: smtpPort,
      secure: isSecure,
      auth: {
        user: process.env.SMTP_USER,
        pass: process.env.SMTP_PASS,
      },
      // TLS configuration with better compatibility for cPanel and other providers
      tls: {
        // Don't fail on invalid certificates (useful for self-signed certs in cPanel)
        rejectUnauthorized: process.env.SMTP_REJECT_UNAUTHORIZED === "true",
        // Require TLS upgrade (for STARTTLS)
        requireTLS: requireTLS,
        // Ignore TLS errors (for problematic certificates)
        ignoreTLS: ignoreTLS,
        // Minimum TLS version (1.2 or higher recommended)
        minVersion: process.env.SMTP_TLS_MIN_VERSION ?? "TLSv1.2",
        // Maximum TLS version
        maxVersion: process.env.SMTP_TLS_MAX_VERSION,
      },
      // Connection timeout
      connectionTimeout: Number.parseInt(
        process.env.SMTP_CONNECTION_TIMEOUT ?? "30000",
        10,
      ),
      // Socket timeout
      socketTimeout: Number.parseInt(
        process.env.SMTP_SOCKET_TIMEOUT ?? "30000",
        10,
      ),
      // Greeting timeout
      greetingTimeout: Number.parseInt(
        process.env.SMTP_GREETING_TIMEOUT ?? "30000",
        10,
      ),
      // Pool settings
      pool: process.env.SMTP_POOL !== "false",
      maxConnections: Number.parseInt(
        process.env.SMTP_MAX_CONNECTIONS ?? "5",
        10,
      ),
      maxMessages: Number.parseInt(process.env.SMTP_MAX_MESSAGES ?? "10", 10),
      // Debug mode (set SMTP_DEBUG=true to see SMTP communication)
      debug: process.env.SMTP_DEBUG === "true",
      logger: process.env.SMTP_DEBUG === "true" ? logger : undefined,
    } as EmailConfig);
  }

  /**
   * Verify SMTP connection with automatic fallback for SSL/TLS errors
   */
  private async verifyConnection(): Promise<void> {
    try {
      await this.transporter.verify();
      this.isConnectionVerified = true;
      logger.info("Email service connected successfully");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const errorCode = (error as any)?.code;

      // Handle SSL/TLS version mismatch - try alternative configuration
      if (
        errorCode === "EPROTO" ||
        errorMessage.includes("WRONG_VERSION_NUMBER") ||
        errorMessage.includes("SSL routines")
      ) {
        logger.warn(
          "Email service SSL/TLS error detected. Attempting alternative configuration...",
        );

        // Try to fix by toggling secure mode if port is 587
        const smtpPort = Number.parseInt(process.env.SMTP_PORT ?? "587");
        if (smtpPort === 587 && process.env.SMTP_SECURE === undefined) {
          logger.info(
            "Retrying with SMTP_SECURE=false and SMTP_REQUIRE_TLS=true for port 587",
          );
          // Update environment temporarily for this attempt
          const originalSecure = process.env.SMTP_SECURE;
          const originalRequireTLS = process.env.SMTP_REQUIRE_TLS;

          process.env.SMTP_SECURE = "false";
          process.env.SMTP_REQUIRE_TLS = "true";

          try {
            // Close existing transporter
            this.transporter.close();
            // Create new transporter with corrected settings
            this.transporter = this.createTransporter();
            await this.transporter.verify();
            this.isConnectionVerified = true;
            logger.info(
              "Email service connected successfully with alternative SSL/TLS configuration",
            );
            return;
          } catch {
            // Restore original settings
            if (originalSecure === undefined) {
              delete process.env.SMTP_SECURE;
            } else {
              process.env.SMTP_SECURE = originalSecure;
            }
            if (originalRequireTLS === undefined) {
              delete process.env.SMTP_REQUIRE_TLS;
            } else {
              process.env.SMTP_REQUIRE_TLS = originalRequireTLS;
            }
          }
        }

        logger.error(
          "Email service SSL/TLS error - Configuration issue detected:",
          {
            message: errorMessage,
            currentConfig: {
              port: smtpPort,
              secure: process.env.SMTP_SECURE,
              requireTLS: process.env.SMTP_REQUIRE_TLS,
            },
            suggestions: [
              "For port 587: Set SMTP_SECURE=false and SMTP_REQUIRE_TLS=true",
              "For port 465: Set SMTP_SECURE=true",
              "Try setting SMTP_REJECT_UNAUTHORIZED=false for self-signed certificates",
              "Check your cPanel email settings for correct SMTP configuration",
            ],
          },
        );
      } else if (errorCode === "ECONNREFUSED") {
        logger.error(
          "Email service connection refused - Check SMTP host and port:",
          errorMessage,
        );
      } else if (errorCode === "EAUTH") {
        logger.error(
          "Email service authentication failed - Check SMTP_USER and SMTP_PASS:",
          errorMessage,
        );
      } else {
        logger.error("Email service connection failed:", error);
      }

      this.isConnectionVerified = false;
    }
  }

  /**
   * Send OTP email for registration
   */
  async sendRegistrationOTP(data: OTPEmailData): Promise<boolean> {
    try {
      const template = this.getRegistrationOTPTemplate(data);

      await this.transporter.sendMail({
        from: `${this.appName} <${this.fromEmail}>`,
        to: data.email,
        subject: template.subject,
        html: template.html,
        text: template.text,
      });

      logger.info(`Registration OTP sent to ${data.email}`);
      return true;
    } catch (error) {
      logger.error(`Failed to send registration OTP to ${data.email}:`, error);
      return false;
    }
  }

  /**
   * Send OTP email for password reset
   */
  async sendPasswordResetOTP(data: OTPEmailData): Promise<boolean> {
    try {
      const template = this.getPasswordResetOTPTemplate(data);

      await this.transporter.sendMail({
        from: `${this.appName} <${this.fromEmail}>`,
        to: data.email,
        subject: template.subject,
        html: template.html,
        text: template.text,
      });

      logger.info(`Password reset OTP sent to ${data.email}`);
      return true;
    } catch (error) {
      logger.error(
        `Failed to send password reset OTP to ${data.email}:`,
        error,
      );
      return false;
    }
  }

  /**
   * Send welcome email after successful registration
   */
  async sendWelcomeEmail(
    email: string,
    name: string,
    role: string,
  ): Promise<boolean> {
    try {
      const template = this.getWelcomeEmailTemplate(name, role);

      await this.transporter.sendMail({
        from: `${this.appName} <${this.fromEmail}>`,
        to: email,
        subject: template.subject,
        html: template.html,
        text: template.text,
      });

      logger.info(`Welcome email sent to ${email}`);
      return true;
    } catch (error) {
      logger.error(`Failed to send welcome email to ${email}:`, error);
      return false;
    }
  }

  /**
   * Send profile approval notification
   */
  async sendApprovalNotification(
    email: string,
    name: string,
    role: "student" | "teacher" | "school",
    status: "approved" | "rejected",
    note?: string,
  ): Promise<boolean> {
    try {
      const template = this.getApprovalNotificationTemplate(
        name,
        role,
        status,
        note,
      );

      await this.transporter.sendMail({
        from: `${this.appName} <${this.fromEmail}>`,
        to: email,
        subject: template.subject,
        html: template.html,
        text: template.text,
      });

      logger.info(`Approval notification sent to ${email}`);
      return true;
    } catch (error) {
      logger.error(`Failed to send approval notification to ${email}:`, error);
      return false;
    }
  }

  /**
   * Get registration OTP email template
   */
  private getRegistrationOTPTemplate(data: OTPEmailData): EmailTemplate {
    const greeting = data.name ? `Hi ${data.name}` : "Hi there";

    const subject = `Verify your ${this.appName} account`;

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333; text-align: center;">${this.appName}</h1>
        <h2 style="color: #666;">Email Verification</h2>
        <p>${greeting},</p>
        <p>Thank you for registering with ${this.appName}! Please use the following OTP to verify your email address:</p>
        <div style="background: #f5f5f5; padding: 20px; text-align: center; margin: 20px 0;">
          <h1 style="color: #333; font-size: 32px; margin: 0; letter-spacing: 8px;">${data.otp}</h1>
        </div>
        <p>This OTP will expire in ${data.expiryMinutes} minutes.</p>
        <p>If you didn't request this verification, please ignore this email.</p>
        <br>
        <p>Best regards,<br>The ${this.appName} Team</p>
      </div>
    `;

    const text = `
      ${greeting},

      Thank you for registering with ${this.appName}!

      Your verification code is: ${data.otp}

      This OTP will expire in ${data.expiryMinutes} minutes.

      If you didn't request this verification, please ignore this email.

      Best regards,
      The ${this.appName} Team
    `;

    return { subject, html, text };
  }

  /**
   * Get password reset OTP email template
   */
  private getPasswordResetOTPTemplate(data: OTPEmailData): EmailTemplate {
    const greeting = data.name ? `Hi ${data.name}` : "Hi there";

    const subject = `Reset your ${this.appName} password`;

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333; text-align: center;">${this.appName}</h1>
        <h2 style="color: #666;">Password Reset</h2>
        <p>${greeting},</p>
        <p>You requested to reset your password. Please use the following OTP to complete the process:</p>
        <div style="background: #f5f5f5; padding: 20px; text-align: center; margin: 20px 0;">
          <h1 style="color: #333; font-size: 32px; margin: 0; letter-spacing: 8px;">${data.otp}</h1>
        </div>
        <p>This OTP will expire in ${data.expiryMinutes} minutes.</p>
        <p>If you didn't request a password reset, please ignore this email and your password will remain unchanged.</p>
        <br>
        <p>Best regards,<br>The ${this.appName} Team</p>
      </div>
    `;

    const text = `
      ${greeting},

      You requested to reset your password.

      Your reset code is: ${data.otp}

      This OTP will expire in ${data.expiryMinutes} minutes.

      If you didn't request a password reset, please ignore this email.

      Best regards,
      The ${this.appName} Team
    `;

    return { subject, html, text };
  }

  /**
   * Get welcome email template
   */
  private getWelcomeEmailTemplate(name: string, role: string): EmailTemplate {
    const subject = `Welcome to ${this.appName}!`;

    const roleSpecificMessage =
      {
        student:
          "You can now explore courses, connect with teachers, and start your learning journey.",
        teacher:
          "You can now create courses, manage students, and share your knowledge.",
        school:
          "You can now manage your institution, approve teachers and students.",
        admin: "You now have full administrative access to the platform.",
      }[role] || "Welcome to our educational platform!";

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333; text-align: center;">${this.appName}</h1>
        <h2 style="color: #666;">Welcome aboard!</h2>
        <p>Hi ${name},</p>
        <p>Welcome to ${this.appName}! Your account has been successfully verified.</p>
        <p>${roleSpecificMessage}</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${process.env.FRONTEND_URL}" style="background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px;">Get Started</a>
        </div>
        <p>If you have any questions, feel free to reach out to our support team.</p>
        <br>
        <p>Best regards,<br>The ${this.appName} Team</p>
      </div>
    `;

    const text = `
      Hi ${name},

      Welcome to ${this.appName}! Your account has been successfully verified.

      ${roleSpecificMessage}

      Visit ${process.env.FRONTEND_URL} to get started.

      If you have any questions, feel free to reach out to our support team.

      Best regards,
      The ${this.appName} Team
    `;

    return { subject, html, text };
  }

  /**
   * Get approval notification email template
   */
  private getApprovalNotificationTemplate(
    name: string,
    role: "student" | "teacher" | "school",
    status: "approved" | "rejected",
    note?: string,
  ): EmailTemplate {
    const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
    const subject = `${roleLabel} Profile ${status === "approved" ? "Approved" : "Rejected"} - ${this.appName}`;

    const statusMessage =
      status === "approved"
        ? `Great news! Your ${role} profile has been approved and is now active.`
        : `We regret to inform you that your ${role} profile has been rejected.`;

    const nextSteps =
      status === "approved"
        ? "You can now access all features available to your role."
        : "Please review the feedback and resubmit your application with the necessary corrections.";

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333; text-align: center;">${this.appName}</h1>
        <h2 style="color: ${status === "approved" ? "#28a745" : "#dc3545"};">${roleLabel} Profile ${status === "approved" ? "Approved" : "Rejected"}</h2>
        <p>Hi ${name},</p>
        <p>${statusMessage}</p>
        ${
          note
            ? `<div style="background: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; margin: 20px 0;">
          <strong>Note:</strong> ${note}
        </div>`
            : ""
        }
        <p>${nextSteps}</p>
        ${
          status === "approved"
            ? `
        <div style="text-align: center; margin: 30px 0;">
          <a href="${process.env.FRONTEND_URL}" style="background: #28a745; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px;">Access Dashboard</a>
        </div>
        `
            : ""
        }
        <p>If you have any questions, please contact our support team.</p>
        <br>
        <p>Best regards,<br>The ${this.appName} Team</p>
      </div>
    `;

    const text = `
      Hi ${name},

      ${statusMessage}

      ${note ? `Note: ${note}` : ""}

      ${nextSteps}

      ${status === "approved" ? `Visit ${process.env.FRONTEND_URL} to access your dashboard.` : ""}

      If you have any questions, please contact our support team.

      Best regards,
      The ${this.appName} Team
    `;

    return { subject, html, text };
  }
}

// Export singleton instance
export const emailService = new EmailService();
